import { LibraryRuleMetadata } from 'schemas/LibraryRule.schema.ts';
import {
  awarenessConditionTargetLabels,
  vaccineConditionTargetLabels,
} from 'utils/formattingUtils';
import { AttributeMetadata } from 'schemas/metadata/attributes.ts';
import { OperatorMetadata } from 'schemas/metadata/operators.ts';
import { IterationRule } from 'schemas/IterationRule.schema.ts';
import { IRuleState } from './ruleReducer.tsx';

export function getAttributeLevels(attributes: AttributeMetadata[]): string[] {
  let levels = [...new Set(attributes.map((attribute) => attribute.AttributeLevel))];

  if (levels.includes('PERSON')) {
    levels = ['PERSON', ...levels.filter((level) => level !== 'PERSON')];
  }

  return levels;
}

export function attributesHaveConditionType(attributes: AttributeMetadata[], attributeLevel: string): boolean {
  if (attributeLevel === 'COHORT') {
    return false;
  }

  return attributes.filter((attribute) => attribute.AttributeLevel === attributeLevel).some((attribute) => attribute.ConditionType !== 'ALL');
}

export function getAttributeConditionTypesByLevel(attributes: AttributeMetadata[], level: string): string[] {
  if (level === 'TARGET') {
    return [
      ...Object.keys(awarenessConditionTargetLabels),
      ...Object.keys(vaccineConditionTargetLabels),
    ];
  }

  const filteredAttributes = attributes.filter((attribute) => attribute.AttributeLevel === level);

  const dataSet = [...new Set(filteredAttributes.map((attribute) => attribute.ConditionType))];

  const filteredDataSet = dataSet.filter((conditionType) => vaccineConditionTargetLabels[conditionType] || awarenessConditionTargetLabels[conditionType]);

  if (filteredDataSet.length === 0) {
    filteredDataSet.push('');
  }

  return filteredDataSet.sort();
}

export function getAttributesForLevel(attributes: AttributeMetadata[], level: string, conditionType: string | undefined): AttributeMetadata[] {
  const filteredAttributes = attributes.filter((attribute) => attribute.AttributeLevel === level);

  if (!conditionType || conditionType === 'ALL') {
    return filteredAttributes;
  }

  return filteredAttributes.filter((attribute) => attribute.ConditionType === conditionType);
}

export function getOperatorsForAttribute(operators: OperatorMetadata[], attribute: AttributeMetadata): OperatorMetadata[] {
  return operators.filter((operator) => operator.ApplicableDataTypes.split(',').includes(attribute.DataType));
}

function getOperatorByName(operators: OperatorMetadata[], operatorName: string): OperatorMetadata {
  return operators.find((operator) => operator.Operator === operatorName) || operators[0];
}

export function buildInitialRuleState(attributes: AttributeMetadata[], operators: OperatorMetadata[], rule: LibraryRuleMetadata | IterationRule | undefined, campaignTarget : string): IRuleState {
  const initialAttributeLevels = getAttributeLevels(attributes);

  // If no existing rule is passed in, build up a default initial state
  if (!rule) {
    const initialConditionTypes = getAttributeConditionTypesByLevel(attributes, initialAttributeLevels[0]);
    const initialAttributes = getAttributesForLevel(attributes, initialAttributeLevels[0], initialConditionTypes[0]);

    return {
      selectedAttributeLevel: initialAttributeLevels[0],
      selectedConditionType: initialConditionTypes[0],
      selectedAttribute: getAttributesForLevel(attributes, initialAttributeLevels[0], initialConditionTypes[0])[0],
      selectedOperator: getOperatorsForAttribute(operators, getAttributesForLevel(attributes, initialAttributeLevels[0], 'ALL')[0])[0],
      selectedComparator: '',
      availableLevels: initialAttributeLevels,
      availableConditionTypes: initialConditionTypes,
      availableAttributes: initialAttributes,
      availableOperators: getOperatorsForAttribute(operators, initialAttributes[0]),
      name: '',
      description: '',
    };
  }

  const attributeLevel = rule.AttributeLevel || 'PERSON';
  let overriddenLibraryTarget = 'ALL';

  if (attributeLevel === 'TARGET') {
    overriddenLibraryTarget = !rule.AttributeTarget || rule.AttributeTarget[0] === '~' ? campaignTarget : rule.AttributeTarget;
  }

  const selectedAttribute = attributes.find((attribute) => attribute.AttributeName === rule.AttributeName && attribute.ConditionType === overriddenLibraryTarget) || attributes.find((attribute) => attribute.ConditionType === overriddenLibraryTarget) || attributes[0];
  const availableAttributes = getAttributesForLevel(attributes, attributeLevel, overriddenLibraryTarget);
  const availableOperators = getOperatorsForAttribute(operators, selectedAttribute);

  return {
    selectedAttributeLevel: attributeLevel,
    selectedConditionType: overriddenLibraryTarget,
    selectedAttribute,
    selectedOperator: getOperatorByName(operators, rule.Operator || ''),
    selectedComparator: rule.Comparator || '',
    availableLevels: initialAttributeLevels,
    availableConditionTypes: getAttributeConditionTypesByLevel(attributes, attributeLevel),
    availableAttributes,
    availableOperators,
    name: rule.Name || '',
    description: rule.Description || '',
    cohortLabel: 'CohortLabel' in rule ? rule.CohortLabel : undefined,
  };
}
